import { z } from "zod";

import { smsAgentReplyStateSchema } from "@/src/models/comms/sms-agent-reply-state";
import { SmsConversationStatus } from "@/src/models/comms/sms-conversation";
import { smsMediaAnalysisSchema } from "@/src/models/comms/sms-media-analysis";
import { DatabaseDefaults } from "@/src/models/database-defaults";

export enum MessageDirection {
  INBOUND = "inbound",
  OUTBOUND = "outbound",
}

export enum MessageProcessingStatus {
  PROCESSING = "processing",
  PROCESSED = "processed",
  FAILED = "failed",
  SKIPPED = "skipped",
}

/**
 * This enum is purely used for client-side tracking of message status. To get the most accurate, current status of a message,
 * we would need to poll the Twilio API and update our db records, as they are the ultimate source of truth.
 * If a status is not present, we assume that the message has been recorded in our db and is MessageStatus.SENT.
 */
export enum MessageStatus {
  PENDING = "pending",
  // This means that Twilio has acknowledged receipt of our API call and forwarded the message to the carrier,
  // but not necessarily that the message has been delivered to the recipient.
  SENT = "sent",
  FAILED = "failed",
}

export enum MessageType {
  MEDIA = "media",
  TEXT = "text",
}

export enum MessageIntent {
  APPOINTMENT_BOOKED = "appointment_booked",
  SCHEDULING_REMINDER = "scheduling_reminder",
  VOICE_CSAT = "voice_csat",
  INITIAL_OUTREACH = "initial_outreach",
  SCHEDULING_INVITE = "scheduling_invite",
  NO_RESPONSE_CLOSING = "no_response_closing",
  NO_RESPONSE_FOLLOW_UP = "no_response_follow_up",
  NO_RESPONSE_HAS_APPOINTMENT = "no_response_has_appointment",
  OUTSIDE_BUSINESS_HOURS = "outside_business_hours",
  PATIENT_INTAKE = "patient_intake",
  // An SMS scheduling-agent reply that was auto-sent (fully-agentic cohort) without human review.
  SCHEDULING_AGENT_REPLY = "scheduling_agent_reply",
  AUTOBOOK_CONFIRMATION = "autobook_confirmation",
  UNMONITORED_LINE_NOTICE = "unmonitored_line_notice",
  OUTBOUND_REFERRAL_SMS_FOLLOW_UP = "outbound_referral_sms_follow_up",
  ORDER_SUBMISSION_SMS = "order_submission_sms",
}

export const messageSchema = z.object({
  messageSid: z.string().nullable(),
  fromNumber: z.string(),
  toNumber: z.string(),
  body: z.string().nullable(),
  direction: z.nativeEnum(MessageDirection),
  patientId: z.string().nullable(),
  referralId: z.string().uuid().nullish(),
  accountSid: z.string().nullable(),
  processingStatus: z.nativeEnum(MessageProcessingStatus).nullable(),
  processedClassification: z
    .record(z.string(), z.object({ reasoning: z.string(), text: z.array(z.string()) }))
    .nullable(),
  messageIntent: z.nativeEnum(MessageIntent).nullable(),
  // Exact inbound messages covered by an agent reply, retained across delivery retries.
  replyToMessageIds: z.array(z.string().uuid()).nullish(),
  agentReplyState: smsAgentReplyStateSchema.nullish(),
  sentByUserId: z.string().uuid().nullable(),
  type: z.nativeEnum(MessageType),
  mediaAnalysis: z.array(smsMediaAnalysisSchema).nullable(),
  mediaPaths: z.array(z.string()).default([]),
});

export type Message = z.infer<typeof messageSchema> & DatabaseDefaults & { status?: MessageStatus };

// Map of patientId to inbound and outbound messages to/from that patient
export type Conversations = Record<string, Message[]>;

// Characters of the latest message body the inbox sidebar renders before ellipsizing.
export const CONVERSATION_PREVIEW_LENGTH = 50;

// The sidebar's slice of a patient's newest message. `body` carries only the first
// CONVERSATION_PREVIEW_LENGTH + 1 characters.
export type ConversationLatestMessage = Pick<Message, "createdAt" | "direction" | "body" | "type"> & {
  mediaPaths: string[] | null;
};

// Lightweight per-patient summary for the inbox sidebar: just the latest message
// plus a couple of derived counts. Lets the sidebar render its list without ever
// downloading every message for every patient in the group.
export const conversationSummarySchema = z.object({
  patientId: z.string(),
  latestMessage: z.custom<ConversationLatestMessage>(),
  messageCount: z.number(),
  isActive: z.boolean(),
  // Lifecycle status of the patient's SMS conversation (source of truth for the
  // inbox's open/completed filters). Null when the patient has no conversation row yet.
  status: z.nativeEnum(SmsConversationStatus).nullable(),
});
export const conversationSummariesSchema = z.record(z.string(), conversationSummarySchema);
export type ConversationSummary = z.infer<typeof conversationSummarySchema>;
export type ConversationSummaries = z.infer<typeof conversationSummariesSchema>;

export const getMessageLabels = (message: Message): Record<string, string> => {
  return Object.fromEntries(Object.entries(message).map(([key, value]) => [`message_${key}`, String(value)]));
};

export const messageShape: Message = {
  id: "",
  messageSid: null,
  fromNumber: "",
  toNumber: "",
  body: null,
  direction: MessageDirection.INBOUND,
  patientId: null,
  referralId: null,
  accountSid: null,
  processedClassification: null,
  messageIntent: null,
  replyToMessageIds: null,
  agentReplyState: null,
  sentByUserId: null,
  groupId: "",
  createdAt: "",
  processingStatus: MessageProcessingStatus.PROCESSING,
  updatedAt: "",
  deletedAt: null,
  type: MessageType.TEXT,
  mediaAnalysis: null,
  mediaPaths: [],
};
